import { apiFetch } from "./client";
import type {
  AlertCreateRequest,
  AlertResponse,
  Coinbase24hStatsResponse,
  DailyExtremesResponse,
  HealthResponse,
  HistoryResponse,
  SubscriptionStatusResponse,
  VolumeHistoryResponse,
} from "./types";

export function getHealth(): Promise<HealthResponse> {
  return apiFetch<HealthResponse>("/healthz");
}

// ADR-0013: /v1/current and /v1/daily-extremes take OPTIONAL auth -- the
// token (when a session exists) is what earns `entitled: true` + unredacted
// R-HOD/R-LOD/depth fields. `auth: true` here only attaches the header when
// a token is actually stored (see client.ts), so logged-out requests are
// byte-identical to before. Invalid/expired tokens degrade server-side to
// entitled:false, never 401, so the free clock keeps working regardless.
export function getCurrent(bandWidthUsd?: number): Promise<import("./types").Tick> {
  const qs = bandWidthUsd ? `?band_width_usd=${bandWidthUsd}` : "";
  return apiFetch(`/v1/current${qs}`, { auth: true });
}

export function getDailyExtremes(bandWidthUsd?: number): Promise<DailyExtremesResponse> {
  const qs = bandWidthUsd ? `?band_width_usd=${bandWidthUsd}` : "";
  return apiFetch<DailyExtremesResponse>(`/v1/daily-extremes${qs}`, { auth: true });
}

export function get24hStats(): Promise<Coinbase24hStatsResponse> {
  return apiFetch<Coinbase24hStatsResponse>("/v1/24h-stats");
}

export function getVolumeHistory(days = 7): Promise<VolumeHistoryResponse> {
  return apiFetch<VolumeHistoryResponse>(`/v1/volume-history?days=${days}`);
}

// `afterTs` (with an explicit `resolution`, so server auto-selection can't
// switch tables mid-series) asks for only the rows strictly newer than that
// ISO timestamp -- the incremental-refresh path that keeps the 60s chart
// poll at ~KBs instead of re-sending the whole window.
// `bandWidthUsd` (research bands, admin-only server-side) is appended only
// when given, so the default-band request stays byte-identical.
export function getHistory(
  minutes: number,
  signal?: AbortSignal,
  opts?: { resolution?: string; afterTs?: string; bandWidthUsd?: number }
): Promise<HistoryResponse> {
  let url = `/v1/history?minutes=${Math.ceil(minutes)}`;
  if (opts?.bandWidthUsd) url += `&band_width_usd=${opts.bandWidthUsd}`;
  if (opts?.resolution) url += `&resolution=${encodeURIComponent(opts.resolution)}`;
  if (opts?.afterTs) url += `&after_ts=${encodeURIComponent(opts.afterTs)}`;
  return apiFetch<HistoryResponse>(url, {
    auth: true,
    signal,
  });
}

// GET /v1/subscription/status (auth required). The dashboard reads the
// `admin` flag off this to decide the chart's max period range (admins get
// the full/unrestricted range; standard subscribers are capped to <=48h so
// they never trigger a 400 from the role-tiered /v1/history clamp).
export function getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
  return apiFetch<SubscriptionStatusResponse>("/v1/subscription/status", { auth: true });
}

/** SW-14 one-click bug report: emailed to support by the API (auth required). */
export interface BugReportRequest {
  client: "web";
  app_version: string;
  description: string;
  context: Record<string, string>;
  recent_errors: string[];
}
export function sendBugReport(body: BugReportRequest): Promise<void> {
  return apiFetch<void>("/v1/support/bug-report", { method: "POST", body, auth: true });
}

export function listAlerts(): Promise<AlertResponse[]> {
  return apiFetch<AlertResponse[]>("/v1/alerts", { auth: true });
}

export function createAlert(body: AlertCreateRequest): Promise<AlertResponse> {
  return apiFetch<AlertResponse>("/v1/alerts", { method: "POST", body, auth: true });
}

export function deleteAlert(id: number): Promise<void> {
  return apiFetch<void>(`/v1/alerts/${id}`, { method: "DELETE", auth: true });
}
